import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";

const titleWordVariants = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  show: (i) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { delay: 0.15 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

const bulletVariants = {
  hidden: { opacity: 0, x: -24 },
  show: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.5 + i * 0.35, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

export function AnimatedStage({ section, courseTitle, idx, total, progress = 0, playing = false, loadingAudio = false }) {
  const bullets = section.bullets || [];
  // karaoke-style active bullet driven by narration progress
  const activeBullet = bullets.length ? Math.min(bullets.length - 1, Math.floor((progress / 100) * bullets.length)) : -1;
  const words = (section.title || "").split(" ");

  return (
    <div
      className="aspect-video bg-[#080818] border border-white/10 relative overflow-hidden flex flex-col justify-center px-8 md:px-16"
      data-testid="video-stage"
    >
      {/* Cinematic animated glow */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(60% 60% at 20% 30%, hsl(var(--primary)/0.28), transparent 70%)",
            "radial-gradient(60% 60% at 75% 65%, hsl(var(--primary)/0.28), transparent 70%)",
            "radial-gradient(60% 60% at 20% 30%, hsl(var(--primary)/0.28), transparent 70%)",
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* subtle grid texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Course title + chapter counter */}
      <div className="absolute top-6 left-8 right-8 z-10 flex items-center gap-3">
        {courseTitle && (
          <span
            className="text-xs md:text-sm font-display font-bold text-white/80 truncate max-w-[45%]"
            data-testid="stage-course-title"
            title={courseTitle}
          >
            {courseTitle}
          </span>
        )}
        <span className="h-3 w-px bg-white/20 shrink-0" aria-hidden />
        <AnimatePresence mode="popLayout">
          <motion.span
            key={idx}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4 }}
            className="text-xs uppercase tracking-[0.2em] text-primary font-semibold shrink-0"
            data-testid="stage-chapter-counter"
          >
            Chapter {idx + 1} / {total}
          </motion.span>
        </AnimatePresence>
        {playing && (
          <span className="flex items-end gap-0.5 h-3" aria-hidden>
            {[0, 1, 2].map((b) => (
              <motion.span
                key={b}
                className="w-0.5 bg-primary"
                animate={{ height: ["30%", "100%", "30%"] }}
                transition={{ duration: 0.7, repeat: Infinity, delay: b * 0.15, ease: "easeInOut" }}
                style={{ height: "30%" }}
              />
            ))}
          </span>
        )}
      </div>

      {/* Content, re-mounts per chapter for cross-fade */}
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="relative z-10"
        >
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter leading-tight mb-6 flex flex-wrap">
            {words.map((w, i) => (
              <motion.span
                key={`${idx}-${i}`}
                custom={i}
                variants={titleWordVariants}
                initial="hidden"
                animate="show"
                className="mr-[0.28em] inline-block"
              >
                {w}
              </motion.span>
            ))}
          </h2>
          <ul className="space-y-3 max-w-2xl">
            {bullets.map((b, i) => (
              <motion.li
                key={`${idx}-${i}`}
                custom={i}
                variants={bulletVariants}
                initial="hidden"
                animate="show"
                className={`flex items-start gap-3 text-base md:text-lg transition-colors duration-300 ${
                  i === activeBullet && playing ? "text-white" : "text-white/70"
                }`}
                data-testid={`stage-bullet-${i}`}
              >
                <motion.span
                  className="mt-2 h-1.5 w-1.5 bg-primary shrink-0"
                  animate={i === activeBullet && playing ? { scale: [1, 1.9, 1] } : { scale: 1 }}
                  transition={{ duration: 1.2, repeat: i === activeBullet && playing ? Infinity : 0 }}
                />
                {b}
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>

      {loadingAudio && (
        <div className="absolute bottom-6 left-8 z-10 flex items-center gap-2 text-white/50 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading narration...
        </div>
      )}
    </div>
  );
}
